/**
 * StuffedStore Theme JavaScript
 * Main theme functionality
 *
 * TODO: Migrate functionality from:
 * - site/scripts/cart-client.ts
 * - site/scripts/analytics-client.ts
 * - Various Preact components converted to vanilla JS
 */

// Theme-level settings provided by Liquid
const themeSettings = window.themeSettings || {};
const prefersReducedMotion = window.matchMedia
  ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
  : false;

// Initialize theme when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  console.log('StuffedStore theme loaded');
  initializeTheme();
});

function initializeTheme() {
  // Initialize any theme-wide functionality here
  if (themeSettings.animationsEnabled !== false) {
    initHeroAnimation();
  } else {
    const hero = document.querySelector('.hero-animate');
    hero?.classList.add('is-visible');
  }

  if (themeSettings.animationsEnabled !== false && themeSettings.scrollRevealEnabled !== false) {
    initScrollReveal();
  } else {
    document.querySelectorAll('[data-reveal], [data-reveal-stagger]').forEach((el) => {
      el.classList.add('is-revealed');
    });
  }

  if (themeSettings.quickAddEnabled !== false) {
    initQuickAdd();
  }

  initNewsletterPopup();
  initCartQuantityButtons();
}

// Expose global StuffedStore object for theme utilities
window.StuffedStore = window.StuffedStore || {};

/**
 * Hero entrance animation - triggers on page load
 */
function initHeroAnimation() {
  const hero = document.querySelector('.hero-animate');
  if (!hero) return;

  if (prefersReducedMotion) {
    hero.classList.add('is-visible');
    return;
  }

  // Trigger animation after a tiny delay for paint
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      hero.classList.add('is-visible');
    });
  });
}

/**
 * Scroll reveal animation using Intersection Observer
 */
function initScrollReveal() {
  const revealElements = document.querySelectorAll('[data-reveal], [data-reveal-stagger]');
  if (!revealElements.length) return;

  // Respect prefers-reduced-motion
  if (prefersReducedMotion) {
    revealElements.forEach(el => el.classList.add('is-revealed'));
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      }
    });
  }, {
    threshold: 0.15,
    rootMargin: '0px 0px -50px 0px'
  });

  revealElements.forEach(el => observer.observe(el));
}

/**
 * Utility: Format money
 */
window.StuffedStore.formatMoney = function(cents, currency = 'USD') {
  const dollars = cents / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency
  }).format(dollars);
};

/**
 * Utility: Fetch cart
 */
window.StuffedStore.getCart = async function() {
  try {
    const response = await fetch('/cart.js');
    return await response.json();
  } catch (error) {
    console.error('Error fetching cart:', error);
    return null;
  }
};

/**
 * Utility: Add to cart
 */
window.StuffedStore.addToCart = async function(variantId, quantity = 1) {
  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: quantity
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const item = await response.json();

    // Dispatch custom event for cart drawer to listen
    document.dispatchEvent(new CustomEvent('cart:add', { detail: item }));

    return item;
  } catch (error) {
    console.error('Error adding to cart:', error);
    throw error;
  }
};

/**
 * Quick Add buttons for carousel product cards
 */
function initQuickAdd() {
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-quick-add]');
    if (!button || button.disabled) return;

    const variantId = button.dataset.variantId;
    const productTitle = button.dataset.productTitle;
    if (!variantId) return;

    const originalText = button.textContent;
    button.classList.add('is-loading');
    button.textContent = 'Adding...';

    try {
      await window.StuffedStore.addToCart(variantId, 1);

      // Success state
      button.classList.remove('is-loading');
      button.classList.add('is-success');
      button.textContent = 'Added!';

      // Show toast
      showQuickAddToast(productTitle);

      // Update cart count
      const cart = await window.StuffedStore.getCart();
      const countElements = document.querySelectorAll('[data-cart-count]');
      countElements.forEach(el => {
        el.textContent = cart.item_count;
        el.style.display = cart.item_count > 0 ? 'flex' : 'none';
      });

      setTimeout(() => {
        button.classList.remove('is-success');
        button.textContent = originalText;
      }, 1500);

    } catch (error) {
      console.error('Quick add error:', error);
      button.classList.remove('is-loading');
      button.textContent = 'Error';
      setTimeout(() => {
        button.textContent = originalText;
      }, 1500);
    }
  });
}

/**
 * Toast notification for Quick Add
 */
function showQuickAddToast(productTitle) {
  const existingToast = document.querySelector('.toast');
  if (existingToast) existingToast.remove();

  const toast = document.createElement('div');
  toast.className = 'toast toast--success';
  toast.setAttribute('role', 'status');
  toast.setAttribute('aria-live', 'polite');
  toast.innerHTML = `
    <svg class="toast__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke-width="2" stroke-linecap="round"/>
      <polyline points="22 4 12 14.01 9 11.01" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
    <span>Added to cart</span>
  `;

  document.body.appendChild(toast);

  setTimeout(() => {
    toast.classList.add('is-leaving');
    setTimeout(() => toast.remove(), 200);
  }, 3000);
}

/**
 * Cart page quantity buttons (CSP-safe replacement for inline handlers)
 */
function initCartQuantityButtons() {
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-quantity-change]');
    if (!button) return;

    const selector = button.closest('[data-cart-quantity-selector]');
    if (!selector) return;

    const input = selector.querySelector('input[type="number"]');
    if (!input) return;

    const action = button.dataset.quantityChange;
    if (action === 'decrease') {
      input.stepDown();
    } else if (action === 'increase') {
      input.stepUp();
    }

    // Submit the parent form
    const form = button.closest('form');
    if (form) {
      form.submit();
    }
  });
}

/**
 * Newsletter popup with localStorage dismissal (5 days)
 */
function initNewsletterPopup() {
  const popup = document.querySelector('[data-newsletter-popup]');
  if (!popup) return;

  const STORAGE_KEY = 'stuffedstore_newsletter_dismissed';
  const DISMISS_DAYS = 5;
  const closeButtons = popup.querySelectorAll('[data-popup-close]');
  const dialog = popup.querySelector('.newsletter-popup__dialog');
  const overlay = popup.querySelector('.newsletter-popup__overlay');

  const isDismissed = () => {
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (!dismissed) return false;
    const dismissedAt = parseInt(dismissed, 10);
    const daysSince = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
    return daysSince < DISMISS_DAYS;
  };

  const close = () => {
    popup.hidden = true;
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
  };

  const show = () => {
    if (isDismissed()) return;
    popup.hidden = false;
    dialog.focus();
  };

  closeButtons.forEach((btn) => btn.addEventListener('click', close));
  overlay?.addEventListener('click', close);
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !popup.hidden) {
      close();
    }
  });

  // Trigger on scroll depth or timeout, whichever comes first
  const scrollHandler = () => {
    const scrolled = window.scrollY / (document.body.scrollHeight - window.innerHeight);
    if (scrolled > 0.5) {
      show();
      window.removeEventListener('scroll', scrollHandler);
    }
  };

  setTimeout(show, 45000);
  window.addEventListener('scroll', scrollHandler, { passive: true });
}
